import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";
import { MoonLoader } from "react-spinners";
import clientConfig from "../config";
import { getAuthHeaders } from "../utils/apiFetch";
import styles from "./AccidentReportTable.module.css";
import Button from "../components/UiElements/Button";
import Input from "../components/UiElements/Input";
import ErrorMessage from "../components/UiElements/ErrorMessage";
import DropdownSelect from "../components/DropdownSelect/DropdownSelect";
import { tokens } from "../design-system/tokens";
import { useDraggingScroll } from "../hooks/useDraggingScroll";

type LoginLogUser = {
  _id: string;
  username?: string;
  name?: string;
  phone?: string;
  email?: string;
};

type LoginLog = {
  _id: string;
  userId?: LoginLogUser | string | null;
  identifier: string;
  identifierType?: "phone" | "username";
  success: boolean;
  failureReason?: string;
  userType?: string[];
  ip?: string;
  userAgent?: string;
  createdAt: string;
};

type SuccessFilterOption = { label: string; value: string };

const SUCCESS_FILTER_OPTIONS: SuccessFilterOption[] = [
  { label: "הכל", value: "" },
  { label: "הצלחה", value: "true" },
  { label: "כשלון", value: "false" },
];

const FAILURE_REASON_LABELS: Record<string, string> = {
  user_not_found: "משתמש לא נמצא",
  invalid_credentials: "סיסמה שגויה",
  missing_identifier: "חסר מזהה",
  server_error: "שגיאת שרת",
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const LoginLogs: React.FC = () => {
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchActive, setSearchActive] = useState(false);
  const [successFilter, setSuccessFilter] = useState("");
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const tableScrollProps = useDraggingScroll();

  const selectedSuccessOption = useMemo(
    () =>
      SUCCESS_FILTER_OPTIONS.find((o) => o.value === successFilter) ||
      SUCCESS_FILTER_OPTIONS[0],
    [successFilter]
  );

  const loadLogs = useCallback(
    async (
      pageNum: number = 1,
      append: boolean = false,
      search: string = searchQuery,
      success: string = successFilter
    ) => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          page: String(pageNum),
          limit: "50",
        });
        if (search.trim()) params.set("search", search.trim());
        if (success === "true" || success === "false") {
          params.set("success", success);
        }

        const response = await fetch(
          `${clientConfig.backendUrl}/users/login-logs?${params}`,
          { headers: getAuthHeaders() }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            (errorData as { message?: string }).message ||
              `HTTP error! status: ${response.status}`
          );
        }

        const data = await response.json();
        const newLogs: LoginLog[] = data.logs || [];
        const totalPages: number = data.totalPages ?? 1;

        if (append) {
          setLogs((prev) => [...prev, ...newLogs]);
        } else {
          setLogs(newLogs);
        }

        setHasMore(pageNum < totalPages);
        setPage(pageNum);
      } catch (err) {
        setError(err instanceof Error ? err.message : "שגיאה בטעינת יומן הכניסות");
        if (!append) setLogs([]);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [searchQuery, successFilter]
  );

  useEffect(() => {
    loadLogs(1, false, "", successFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successFilter]);

  useEffect(() => {
    if (searchQuery === "") {
      if (searchActive) {
        setSearchActive(false);
        loadLogs(1, false, "", successFilter);
      }
      return;
    }

    if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      setSearchActive(true);
      loadLogs(1, false, searchQuery, successFilter);
    }, 400);

    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleClearSearch = () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
      debounceTimeout.current = null;
    }
    setSearchQuery("");
    setSearchActive(false);
    loadLogs(1, false, "", successFilter);
  };

  const handleLoadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadLogs(page + 1, true, searchQuery, successFilter);
    }
  }, [loading, hasMore, page, loadLogs, searchQuery, successFilter]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);
    return () => {
      if (currentRef) observer.unobserve(currentRef);
      observer.disconnect();
    };
  }, [hasMore, loading, handleLoadMore]);

  const getUserLabel = (log: LoginLog): string => {
    if (!log.userId || typeof log.userId === "string") return "-";
    const u = log.userId;
    return u.name || u.username || u.phone || u.email || "-";
  };

  return (
    <div className={styles.Container} dir="rtl">
      <div className={styles.HeaderContainer}>
        <div className={styles.Title} style={tokens.typography.h2}>
          יומן כניסות
        </div>
        <div className={styles.searchContainer}>
          <Input
            type="text"
            placeholder="חפש לפי טלפון או שם משתמש..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            color="outlineSecondary"
            size="medium"
            rtl
            width="100%"
            maxWidth={320}
          />
          {searchActive ? (
            <Button
              label="נקה"
              color="outlineSecondary"
              size="small"
              width="auto"
              onClick={handleClearSearch}
            />
          ) : null}
          <DropdownSelect
            name="login-success-filter"
            values={[selectedSuccessOption]}
            options={[...SUCCESS_FILTER_OPTIONS]}
            onChange={(selected: SuccessFilterOption[]) => {
              setSuccessFilter(selected?.[0]?.value ?? "");
            }}
            color="outlineSecondary"
            size="medium"
            width={140}
            dropdownMaxHeight={180}
            placeholder="סטטוס"
          />
        </div>
      </div>

      {loading && logs.length === 0 ? (
        <div className={styles.LoadingContainer}>
          <MoonLoader size={50} color={tokens.colors.secondary} />
        </div>
      ) : error ? (
        <div className={styles.ErrorState}>
          <ErrorMessage message={error} size="medium" />
          <Button
            label="נסה שוב"
            color="secondary"
            size="medium"
            width={120}
            onClick={() => loadLogs(1, false)}
          />
        </div>
      ) : (
        <>
          {!loading && logs.length === 0 ? (
            <div className={styles.EmptyState}>אין רשומות כניסה להצגה</div>
          ) : null}
          {logs.length > 0 && (
            <div className={styles.TableWrapper}>
              <div className={styles.TableInner}>
                <div className={styles.TableFrame}>
                  <div
                    ref={tableScrollProps.ref}
                    className={`${styles.TableScroll} ${
                      tableScrollProps.isDraggingScroll
                        ? styles.TableScrollDragging
                        : ""
                    }`}
                    onMouseDown={tableScrollProps.onMouseDown}
                    style={tableScrollProps.style}
                  >
                    <Table className={styles.Table}>
                      <Thead>
                        <Tr className={styles.TrHeader}>
                          <Th className={styles.ThTitle}>זמן</Th>
                          <Th className={styles.ThTitle}>מזהה</Th>
                          <Th className={styles.ThTitle}>סוג</Th>
                          <Th className={styles.ThTitle}>סטטוס</Th>
                          <Th className={styles.ThTitle}>סיבה</Th>
                          <Th className={styles.ThTitle}>משתמש</Th>
                          <Th className={styles.ThTitle}>הרשאות</Th>
                          <Th className={styles.ThTitle}>IP</Th>
                          <Th className={styles.ThTitle}>User Agent</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {logs.map((log) => (
                          <Tr key={log._id} className={styles.TrData}>
                            <Td data-label="זמן">
                              {formatDateTime(log.createdAt)}
                            </Td>
                            <Td data-label="מזהה">{log.identifier || "-"}</Td>
                            <Td data-label="סוג">
                              {log.identifierType === "phone"
                                ? "טלפון"
                                : log.identifierType === "username"
                                  ? "שם משתמש"
                                  : "-"}
                            </Td>
                            <Td data-label="סטטוס">
                              {log.success ? "הצלחה" : "כשלון"}
                            </Td>
                            <Td data-label="סיבה">
                              {log.success
                                ? "-"
                                : FAILURE_REASON_LABELS[log.failureReason || ""] ||
                                  log.failureReason ||
                                  "-"}
                            </Td>
                            <Td data-label="משתמש">{getUserLabel(log)}</Td>
                            <Td data-label="הרשאות">
                              {Array.isArray(log.userType) && log.userType.length
                                ? log.userType.join(", ")
                                : "-"}
                            </Td>
                            <Td data-label="IP">{log.ip || "-"}</Td>
                            <Td
                              data-label="User Agent"
                              className={styles.NotesContainer}
                              title={log.userAgent || ""}
                            >
                              <div className={styles.NotesContent}>
                                {log.userAgent || "-"}
                              </div>
                            </Td>
                          </Tr>
                        ))}
                        {loading && hasMore && (
                          <Tr>
                            <Td colSpan={9} className={styles.LoadingMore}>
                              טוען עוד...
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    </Table>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={loadMoreRef} style={{ height: 1 }} />
        </>
      )}
    </div>
  );
};

export default LoginLogs;
